/**
 * /admin — single landing page that lets you jump to every NoFee admin
 * panel without remembering URLs. Card grid with status indicators (e.g.
 * unread support tickets, pending reimbursements) once the admin key is
 * set in localStorage.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Shield, LogOut, Inbox, Wallet, DollarSign, Activity,
  Mail, Heart, ArrowRight, BarChart3, FileWarning, TrendingUp,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_KEY_LS = 'nofee_admin_key';

const PANELS = [
  {
    key: 'revenue',
    Icon: TrendingUp,
    title: 'Platform revenue',
    desc: 'MRR, ARR, growth time series, churn',
    href: '/admin/revenue',
    statKey: 'mrr',
  },
  {
    key: 'subscriptions',
    Icon: BarChart3,
    title: 'Subscriptions',
    desc: 'Active customers, trials, churn list',
    href: '/admin/subscriptions',
    statKey: 'subs_count',
  },
  {
    key: 'support',
    Icon: Inbox,
    title: 'Support inbox',
    desc: 'Customer messages, reply, status',
    href: '/admin/support',
    statKey: 'open_tickets',
  },
  {
    key: 'reimbursements',
    Icon: Wallet,
    title: 'Reimbursements',
    desc: 'Approve restaurant gift payouts',
    href: '/admin/reimbursements',
    statKey: 'pending_reimbursements',
  },
  {
    key: 'stripe-health',
    Icon: Activity,
    title: 'Stripe health',
    desc: 'Onboarding status across restaurants',
    href: '/admin/stripe-health',
  },
  {
    key: 'engagement',
    Icon: Mail,
    title: 'Go-Live engagement',
    desc: 'Email opens/clicks on launch emails',
    href: '/admin/engagement',
  },
  {
    key: 'pending',
    Icon: FileWarning,
    title: 'Pending payments',
    desc: 'Restaurants stuck in pending state',
    href: '/admin/pending-payments',
  },
];

const formatMRR = (cents) => `$${((cents || 0) / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const AdminHubPage = () => {
  const [adminKey, setAdminKey] = useState(() => { try { return localStorage.getItem(ADMIN_KEY_LS) || ''; } catch { return ''; } });
  const [authed, setAuthed] = useState(!!adminKey);
  const [keyInput, setKeyInput] = useState('');
  const [stats, setStats] = useState({});
  const [error, setError] = useState('');

  const loadStats = async (key) => {
    try {
      const [subs, tickets] = await Promise.all([
        axios.get(`${API}/admin/subscriptions`, { headers: { 'X-Admin-Key': key } }).catch(() => null),
        axios.get(`${API}/admin/support/tickets?status=open`, { headers: { 'X-Admin-Key': key } }).catch(() => null),
      ]);
      const next = {};
      if (subs?.data) {
        next.mrr = formatMRR(subs.data.mrr_cents);
        next.mrr_sub = `${subs.data.mrr_count} active`;
      }
      if (tickets?.data) {
        next.open_tickets = String(tickets.data.counts?.open || 0);
        next.open_tickets_sub = 'unanswered';
      }
      setStats(next);
      setAuthed(true);
    } catch (err) {
      if (err?.response?.status === 403) {
        setError('Invalid admin key.');
        try { localStorage.removeItem(ADMIN_KEY_LS); } catch {}
        setAuthed(false);
      }
    }
  };

  useEffect(() => { if (adminKey) loadStats(adminKey); /* eslint-disable-next-line */ }, []);

  const submitKey = (e) => {
    e.preventDefault();
    const k = keyInput.trim();
    if (!k) return;
    try { localStorage.setItem(ADMIN_KEY_LS, k); } catch {}
    setAdminKey(k);
    loadStats(k);
  };

  const logout = () => {
    try { localStorage.removeItem(ADMIN_KEY_LS); } catch {}
    setAdminKey(''); setAuthed(false); setStats({});
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-4">
        <form
          onSubmit={submitKey}
          data-testid="admin-hub-auth-form"
          className="w-full max-w-md p-7 rounded-3xl border border-white/10 bg-white/[0.02]"
        >
          <div className="flex items-center gap-2 mb-2">
            <Shield className="w-5 h-5 text-yellow-300" />
            <span className="text-[11px] uppercase tracking-[0.22em] text-yellow-300 font-bold">Admin</span>
          </div>
          <h1 className="font-['Outfit',_Inter,_sans-serif] text-2xl font-semibold tracking-tight mb-1">Sign in</h1>
          <p className="text-[13px] text-white/55 mb-5">Enter the NoFee admin key.</p>
          <input
            type="password"
            value={keyInput}
            onChange={(e) => setKeyInput(e.target.value)}
            placeholder="Admin key"
            data-testid="admin-hub-key-input"
            className="w-full px-4 py-3 rounded-xl bg-white/[0.04] border border-white/12 text-white text-[14px] focus:outline-none focus:border-yellow-500/55"
          />
          {error && <p className="mt-3 text-[12.5px] text-rose-300">{error}</p>}
          <button
            type="submit"
            data-testid="admin-hub-key-submit"
            className="mt-4 w-full inline-flex items-center justify-center gap-1.5 px-5 py-3 rounded-xl bg-gradient-to-b from-yellow-300 to-yellow-500 text-black font-extrabold text-[14px]"
          >
            Sign in
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-hub-page">
      <header className="sticky top-0 z-10 bg-black/85 backdrop-blur-md border-b border-white/[0.06]">
        <div className="max-w-[1300px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-yellow-300" />
            <h1 className="text-[16px] font-bold tracking-tight">NoFee Admin</h1>
            <span className="ml-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-yellow-500/30 bg-yellow-500/[0.08] text-[10px] uppercase tracking-[0.2em] text-yellow-300 font-bold">
              Control Center
            </span>
          </div>
          <button
            type="button"
            onClick={logout}
            data-testid="admin-hub-logout"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-white/12 hover:border-rose-500/40 text-white/65 hover:text-rose-300 text-[12.5px] font-bold"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-[1300px] mx-auto px-4 sm:px-6 lg:px-8 py-7">
        <div className="mb-7">
          <h2 className="font-['Outfit',_Inter,_sans-serif] text-3xl sm:text-4xl font-semibold tracking-[-0.03em] leading-[1.05]">
            Welcome back.
          </h2>
          <p className="mt-2 text-[14px] text-white/55">
            Everything you need to run NoFee — subscriptions, support, payouts, and platform health, all in one place.
          </p>
        </div>

        {/* Top stats — at-a-glance */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-7" data-testid="admin-hub-top-stats">
          <Link
            to="/admin/subscriptions"
            data-testid="admin-stat-mrr"
            className="group p-5 rounded-2xl border border-yellow-500/30 bg-gradient-to-br from-yellow-500/[0.06] via-zinc-950 to-black hover:border-yellow-500/55 transition-all"
          >
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-4 h-4 text-yellow-300" />
              <span className="text-[10.5px] uppercase tracking-[0.2em] text-yellow-300 font-bold">Monthly recurring</span>
            </div>
            <div className="text-3xl font-extrabold text-white tabular-nums">{stats.mrr || '$0'}</div>
            <div className="text-[11.5px] text-white/45 mt-0.5">{stats.mrr_sub || '0 active'}</div>
          </Link>
          <Link
            to="/admin/support"
            data-testid="admin-stat-tickets"
            className="group p-5 rounded-2xl border border-white/10 bg-white/[0.02] hover:border-yellow-500/35 transition-all"
          >
            <div className="flex items-center gap-2 mb-2">
              <Inbox className="w-4 h-4 text-amber-300" />
              <span className="text-[10.5px] uppercase tracking-[0.2em] text-amber-300 font-bold">Open tickets</span>
            </div>
            <div className="text-3xl font-extrabold text-white tabular-nums">{stats.open_tickets ?? '—'}</div>
            <div className="text-[11.5px] text-white/45 mt-0.5">{stats.open_tickets_sub || ''}</div>
          </Link>
          <Link
            to="/admin/reimbursements"
            data-testid="admin-stat-reimbursements"
            className="group p-5 rounded-2xl border border-white/10 bg-white/[0.02] hover:border-yellow-500/35 transition-all"
          >
            <div className="flex items-center gap-2 mb-2">
              <Heart className="w-4 h-4 text-rose-300" />
              <span className="text-[10.5px] uppercase tracking-[0.2em] text-rose-300 font-bold">Reimbursements</span>
            </div>
            <div className="text-3xl font-extrabold text-white tabular-nums">→</div>
            <div className="text-[11.5px] text-white/45 mt-0.5">Approve pending</div>
          </Link>
        </div>

        {/* Panel grid */}
        <h2 className="text-[11px] uppercase tracking-[0.22em] text-white/55 font-bold mb-3">All panels</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="admin-hub-panel-grid">
          {PANELS.map(({ key, Icon, title, desc, href }) => (
            <Link
              key={key}
              to={href}
              data-testid={`admin-panel-${key}`}
              className="group p-5 rounded-2xl border border-white/8 bg-white/[0.02] hover:border-yellow-500/40 hover:bg-white/[0.04] transition-all"
            >
              <div className="flex items-start justify-between mb-3">
                <span className="w-10 h-10 rounded-xl bg-yellow-500/10 border border-yellow-500/30 flex items-center justify-center">
                  <Icon className="w-4.5 h-4.5 text-yellow-300" />
                </span>
                <ArrowRight className="w-4 h-4 text-white/30 group-hover:text-yellow-300 group-hover:translate-x-0.5 transition-all" />
              </div>
              <div className="text-[14.5px] font-bold tracking-tight text-white">{title}</div>
              <div className="mt-1 text-[12px] text-white/55">{desc}</div>
            </Link>
          ))}
        </div>
      </main>
    </div>
  );
};

export default AdminHubPage;
